import type { AuthUser, Session } from "@ncp/api-client";

export const ACCESS_STATE_KEY = "econic.access";

export function rememberedAccessState(): string | null {
  try { return window.localStorage.getItem(ACCESS_STATE_KEY); } catch { return null; }
}

type Standing = { userId: string; state: "waitlist" | "admitted" };

export function engagementPollInterval(state: Standing["state"] | undefined, rememberedWaiting: boolean): number | false {
  return state === "waitlist" || rememberedWaiting ? 60_000 : false;
}

function sameSession(before: Session | null, current: Session | null): boolean {
  if (!before || !current || before.user.id !== current.user.id) return false;
  return before.sessionId || current.sessionId
    ? before.sessionId === current.sessionId
    : before.accessToken === current.accessToken;
}

/** Publish admission only after the same session has fresh server-resolved permissions. */
export async function admissionReadyStanding<T extends Standing>(options: {
  wasWaiting: boolean;
  readSession: () => Session | null;
  readStanding: () => Promise<T | null>;
  readUser: () => Promise<AuthUser>;
  updateUser: (user: AuthUser) => void;
}): Promise<T | null> {
  const session = options.readSession();
  const standing = await options.readStanding();
  const assertCurrent = () => {
    if (!sameSession(session, options.readSession()) || (standing && standing.userId !== session?.user.id)) {
      throw new Error("The signed-in session changed while checking access.");
    }
  };
  assertCurrent();
  if (standing?.state === "admitted" && options.wasWaiting) {
    const user = await options.readUser();
    assertCurrent();
    if (user.id !== session?.user.id) throw new Error("The access profile belongs to another account.");
    // Updating the cached profile does not issue, extend, or replace a token.
    options.updateUser(user);
  }
  return standing;
}
